import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../services/firebase';

const checkIsLocal = () => {
    if (typeof window !== 'undefined') {
        if (window.__IS_WEB__) return false;
        if (window.__IS_DESKTOP__) return true;
    }
    return typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local;
};

export function useProjects(organization = null, teams = []) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const orgId = organization?.id || null;

  useEffect(() => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      chrome.storage.local.get(['projects'], (res) => {
        // Initialize with default mock if empty for the extension's preview
        const defaultProjects = res.projects || [
          { id: '1', name: 'All Snippets', count: 0, isExpanded: true, parentId: null }
        ];
        setProjects(defaultProjects);
        setLoading(false);
      });

      const listener = (changes, namespace) => {
        if (namespace === 'local' && changes.projects) {
          setProjects(changes.projects.newValue || []);
        }
      };
      chrome.storage.onChanged.addListener(listener);

      return () => {
        chrome.storage.onChanged.removeListener(listener);
      };
    } else {
      const unsubscribeAuth = auth.onAuthStateChanged(user => {
        if (user) {
          const q = orgId 
            ? query(collection(db, 'projects'), where('orgId', '==', orgId))
            : query(collection(db, 'users', user.uid, 'projects'));
            
          const unsub = onSnapshot(q, (snapshot) => {
            const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetched.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
            setProjects(fetched);
            setLoading(false);
          });
          return () => unsub();
        } else {
          setProjects([]);
          setLoading(false);
        }
      });
      return () => unsubscribeAuth();
    }
  }, [orgId]);

  const buildProjectTree = (items, parentId = null) => {
    return items
      .filter(item => item.parentId === parentId)
      .map(item => ({
        ...item,
        children: buildProjectTree(items, item.id),
      }));
  };

  const projectTree = buildProjectTree(projects);

  const addProject = async (projectData) => {
    const isLocal = checkIsLocal();
    const newId = Date.now().toString();
    const now = new Date().toISOString();
    const newProject = { ...projectData, id: newId, createdAt: now, count: 0, isExpanded: false };

    if (isLocal) {
      return new Promise((resolve) => {
        chrome.storage.local.get(['projects'], (res) => {
          const currentProjects = res.projects || [];
          chrome.storage.local.set({ projects: [...currentProjects, newProject] }, () => resolve(newId));
        });
      });
    }

    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const collectionRef = orgId 
      ? collection(db, 'projects') 
      : collection(db, 'users', auth.currentUser.uid, 'projects');
      
    const docRef = doc(collectionRef);
    await setDoc(docRef, { ...projectData, userId: auth.currentUser.uid, createdAt: now, count: 0, isExpanded: false });
    return docRef.id;
  };

  const removeProjects = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    
    // Auto-resolve children to delete them as well
    const getAllIdsToDelete = (currentIds) => {
      let idsToDelete = new Set(currentIds);
      let addedNew = true;
      while(addedNew) {
        addedNew = false;
        projects.forEach(p => {
          if (idsToDelete.has(p.parentId) && !idsToDelete.has(p.id)) {
            idsToDelete.add(p.id);
            addedNew = true;
          }
        });
      }
      return Array.from(idsToDelete);
    };

    const finalIds = getAllIdsToDelete(ids);

    const isLocal = checkIsLocal();
    if (isLocal) {
      return new Promise((resolve) => {
        chrome.storage.local.get(['projects'], (res) => {
          const currentProjects = res.projects || [];
          const idSet = new Set(finalIds);
          chrome.storage.local.set({ projects: currentProjects.filter(p => !idSet.has(p.id)) }, () => resolve());
        });
      });
    }

    if (!auth.currentUser) throw new Error("Must be logged in");
    
    let batch = writeBatch(db);
    let opCount = 0;

    for (const id of finalIds) {
      const docRef = orgId 
        ? doc(db, 'projects', id) 
        : doc(db, 'users', auth.currentUser.uid, 'projects', id);
        
      batch.delete(docRef);
      opCount++;

      if (opCount >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        opCount = 0;
      }
    }
    
    if (opCount > 0) {
      await batch.commit();
    }
  };

  const toggleProjectExpansion = async (id, currentExpandedState) => {
    const isLocal = checkIsLocal();
    if (isLocal) {
      return new Promise((resolve) => {
        chrome.storage.local.get(['projects'], (res) => {
          const currentProjects = res.projects || [];
          const updated = currentProjects.map(p => p.id === id ? { ...p, isExpanded: !currentExpandedState } : p);
          chrome.storage.local.set({ projects: updated }, () => resolve());
        });
      });
    }

    if (!auth.currentUser) return;
    
    const docRef = orgId 
      ? doc(db, 'projects', id) 
      : doc(db, 'users', auth.currentUser.uid, 'projects', id);
      
    await updateDoc(docRef, { isExpanded: !currentExpandedState });
  };

  return { projects, projectTree, loading, addProject, removeProjects, toggleProjectExpansion };
}
